import { FiSnackBarService } from '@fi-app/shared/fi-snackbar/snack-bar.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { DocumentService } from '../document.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { DocumentModel } from '../document.model';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { ObjectTypes } from '@fi-shared/types';
import { Subject } from 'rxjs';
import { ICommentModel, ICommentsContainer } from '@fi-shared/fi-comments/fi-comments';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCommentsListStateService } from '@fi-shared/fi-comments/fi-comments-list/services/fi-comments-list-state.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

@Component({
    selector: 'fi-document-view-card',
    templateUrl: './document-view-card.component.html',
    styleUrls: ['./document-view-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-view-card' },
    standalone: false,
})
export class DocumentViewCardComponent implements OnInit, OnDestroy {
    @Input() document: DocumentModel;
    @Input() comments: ICommentModel[];
    @Input() templateLayout: TemplateLayoutModel;
    @Input() showComments: boolean;
    @Input() writeAccessRight: boolean;
    @Input() focusNewComment: boolean = false;
    @Input() languageId: number;
    @Input() commentId: number;
    @Output() languageIdChange = new EventEmitter<number>();

    @ViewChild('commentScrollTo', { static: false }) commentScrollToElement: ElementRef;
    @ViewChild('card', { static: false }) cardElement: ElementRef;
    @ViewChild('cardBottomScroll', { static: false }) cardBottomScrollElement: ElementRef;
    commentsExpanded: boolean = false;
    onDocumentLayoutRendered = new EventEmitter<void>();
    commentsContainer: ICommentsContainer;
    ObjectTypes = ObjectTypes;
    FiStateNames = FiStateNames;
    documentLayoutAfterRenderer() {
        this.onDocumentLayoutRendered.emit();
        if (this.document.GeneratePreviewFileImage) {
            const screenHTML = this.htmlPreviewService.getPreviewHtml(document.getElementById('document_view'));
            this.documentService.GenerateDocumentPreviewFileImage(this.document.Id, screenHTML);
        }
    }
    private readonly scrollableElementMobileClass: string = 'MobileTabPageContainer';
    private readonly scrollableElementDesktopClass: string = 'Ccm-Page-Document-View';
    private readonly maxTransitionTimeMS: number = 800;
    private readonly scrollTopOffset: number = 20;

    constructor(
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private detectDevicesService: DetectDevicesService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private documentService: DocumentService,
        private loadingService: LoadingService,
        private fiSnackBarService: FiSnackBarService,
        private htmlPreviewService: HtmlPreviewService,
        private userDataService: FiUserDataService,
        private commentsListStateService: FiCommentsListStateService,
    ) {}

    ngOnInit() {
        /*if (this.focusNewComment) {
            this.toggleComments();
        }*/
        if (this.commentId) {
            this.commentsExpanded = true;
        }
    }

    get commentsCount(): number {
        return this.commentsListStateService.getCommentsCount(ObjectTypes.Document, this.document.Id);
    }

    protected onLanguageIdChanged(languageId: FiApplicationLanguageType) {
        this.languageIdChange.emit(languageId);
    }

    getCommentScrollElement(): HTMLElement {
        return this.detectDevicesService.isMobilePhone()
            ? this.getElementByClassName(this.scrollableElementMobileClass)
            : this.getElementByClassName(this.scrollableElementDesktopClass);
    }

    getElementByClassName(className: string): HTMLElement {
        return Array.from(document.getElementsByClassName(className))[0] as HTMLElement;
    }

    toggleComments() {
        if (!this.commentsExpanded) {
            this.commentsExpanded = true;
            setTimeout(() => {
                if (this.commentsExpanded) {
                    this.scrollToElementAnimateService.scrollToElementBottom(
                        this.commentScrollToElement.nativeElement,
                        this.getCommentScrollElement(),
                        this.scrollTopOffset,
                    );
                    this.commentsListStateService.emitCommentUpdateConnectionLine(
                        ObjectTypes.Document,
                        this.document.Id,
                    );
                }
            });
        } else {
            this.scrollToElementAnimateService.scrollToElementBottom(
                this.cardBottomScrollElement.nativeElement,
                this.getElementByClassName(this.scrollableElementDesktopClass),
            );

            setTimeout(() => (this.commentsExpanded = false), this.maxTransitionTimeMS); //800 - maximum transition time to scroll back to card
        }
    }

    /*
    getHighlightCommentId() {
        return this.stateService.params.commentId;
    }*/

    openRestorePageDialog() {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('document.restore_dialog_header'),
                messageText: this.appTextService.get('document.restore_dialog_message_text'),
                yesButtonText: this.appTextService.get('common.proceed'),
            })
            .onYesButtonClick(() => this.restoreDocument(this.document.Id));
    }

    restoreDocument(id: number) {
        this.loadingService.trackPromise(
            this.documentService.RestoreDocuments([id]).then(() => {
                this.resetDocumentData(id);
                this.showDocumentRestoredSnackbar();
            }),
        );
    }

    private showDocumentRestoredSnackbar() {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('document.document_restored'),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    resetDocumentData(id: number) {
        this.documentService.GetDocument(id).then((data: DocumentModel) => (this.document = data));
    }

    getTrashDocumentText(): string {
        return this.detectDevicesService.isMobilePhone()
            ? `${this.appTextService.get('document.document_in_trash')}.`
            : this.appTextService.get('document.document_in_trash');
    }

    getNotSharedDocumentText(): string {
        return this.appTextService.get('document.document_not_shared');
    }

    getRestoreDocumentText(): string {
        return this.detectDevicesService.isMobilePhone()
            ? this.appTextService.get('document.restore')
            : ` - ${this.appTextService.get('document.restore')}`;
    }

    getToShareDocumentText(): string {
        return ` - ${this.appTextService.get('document.share')}`;
    }

    isRestoreInfoShown(): boolean {
        return this.document.IsDeleted;
    }

    isNotSharedInfoShown(): boolean {
        return !this.document.IsDeleted && !this.document.PublicationSetting.IsShared;
    }

    showSiteDesignAccessForItems(): boolean {
        return this.userDataService.showSiteDesignAccessForItems() && !!this.document.SiteDesignIds.length;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
